import {FC, useEffect} from 'react'
import {gql} from '@apollo/client'
import {yup} from 'libs/yup'
import {yupResolver} from '@hookform/resolvers/yup'
import {defaults} from 'lodash/fp'
import {
  Control,
  FieldPath,
  FormProvider,
  SubmitHandler,
  useController,
  UseControllerProps,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form'
import {useDispatch} from 'react-redux'
import {Link} from 'react-router-dom'
import classNames from 'classnames'
import DateTime from 'react-datetime-3.1.1'
import moment from 'moment'
import Button from 'jbc-front/components/Button'
import ActionButton from 'jbc-front/components/ActionButton'
import {Input} from 'jbc-front/components/presenters/form/Input'
import {ErrorMessage} from 'jbc-front/components/presenters/form/ErrorMessage'
import {Radio, RadioContainer} from 'jbc-front/components/presenters/form/Radio'
import {useMutation, useQuery} from 'hooks/graphql'
import {eachValidationErrorsForHookFrom, ServerValidationError} from 'hooks/graphql/error/ServerValidationError'
import {Body, Panel} from 'components/ui/Panel'
import {Label} from 'components/forms/Label'
import {DateField} from 'components/reactHookForm/DateField'
import {FormErrors} from 'components/reactHookForm/FormErrors'
import {EmployeeName} from 'pages/withAuth/adminPage/procedures/parts/EmployeeName'
import {asyncError} from 'store/actions/asyncError'
import {ReportFormProps} from './ReportForm'
import {laborConsultantSchema, LaborConsultantSection} from './LaborConsultant'
import {useConfirmUpgrade} from './useConfirmUpgrade'
import {ONE_PAGE_ROWS, TWO_PAGE_ROWS, makeRowsData, workingYearAndMonths} from './R510Utils'
import styles from './R510Report.scss'

const R510_REPORT = gql`
  query R510Report($procedureStatusId: ID!) {
    client {
      id
      procedureStatus(procedureStatusId: $procedureStatusId) {
        id
        procedureType
        employee {
          id
          staffCode
          displayFullName
          joinedAt
          currentOffice {
            id
            salaryClosingDate
          }
        }
        r509ParamSet {
          id
          startedOn
        }
        r512ParamSet {
          id
          startedOn
        }
        r510ParamSet {
          id
          notesAboutSalary
          hasContractPeriod
          contractPeriodEndOn
          workingYears
          workingMonths
          needLaborConsultant
          laborConsultantCreateDate
          laborConsultantName
          laborConsultantAgentType
          laborConsultantPrefecture {
            id
          }
          laborConsultantPhoneNumber0
          laborConsultantPhoneNumber1
          laborConsultantPhoneNumber2
          version
          needUpgrade
          r510PaymentParamSets {
            id
            rowNumber
            insuredTermStartOn
            insuredTermEndOn
            insuredBaseDateNum
            paymentTermStartOn
            paymentTermEndOn
            paymentBaseDateNum
            monthlyWage
            hourlyOrDailyWage
            notes
          }
        }
      }
    }
  }
`

interface R510ReportQueryVariables {
  procedureStatusId: string
}

interface R510ReportQueryResult {
  client: {
    id: string
    procedureStatus: {
      id: string
      procedureType: string
      employee: {
        id: string
        staffCode: string
        displayFullName: string
        joinedAt: string
        currentOffice: {
          id: string
          salaryClosingDate: number
        }
      }
      r509ParamSet?: {
        id?: string
        startedOn?: string
      }
      r512ParamSet?: {
        id?: string
        startedOn?: string
      }
      r510ParamSet: {
        id?: string
        notesAboutSalary?: string
        hasContractPeriod?: boolean
        contractPeriodEndOn?: string
        workingYears?: number
        workingMonths?: number
        needLaborConsultant: boolean
        laborConsultantCreateDate?: string
        laborConsultantName?: string
        laborConsultantAgentType?: 'submission_agent' | 'office_agent'
        laborConsultantPrefecture?: {
          id: string
        }
        laborConsultantPhoneNumber0?: string
        laborConsultantPhoneNumber1?: string
        laborConsultantPhoneNumber2?: string
        version: number
        needUpgrade: boolean
        r510PaymentParamSets: {
          id?: string
          rowNumber: number
          insuredTermStartOn?: string
          insuredTermEndOn?: string
          insuredBaseDateNum?: number
          paymentTermStartOn?: string
          paymentTermEndOn?: string
          paymentBaseDateNum?: number
          monthlyWage?: string
          hourlyOrDailyWage?: string
          notes?: string
        }[]
      }
    }
  }
}

const UPDATE_R510_PARAM_SET = gql`
  mutation UpdateR510ParamSet($input: UpdateR510ParamSetInput!) {
    updateR510ParamSet(input: $input) {
      r510ParamSet {
        id
      }
    }
  }
`

interface UpdateR510ParamSetQueryVariables {
  input: {
    procedureStatusId: string
    attributes: {
      r510PaymentParamSets?: {
        rowNumber: number
        insuredTermStartOn?: string | null
        insuredTermEndOn?: string | null
        insuredBaseDateNum?: number | null
        paymentTermStartOn?: string | null
        paymentTermEndOn?: string | null
        paymentBaseDateNum?: number | null
        monthlyWage?: string | null
        hourlyOrDailyWage?: string | null
        notes?: string | null
      }[]
      notesAboutSalary?: string | null
      hasContractPeriod?: boolean | null
      contractPeriodEndOn?: string | null
      workingYears?: number | null
      workingMonths?: number | null
      needLaborConsultant?: boolean | null
      laborConsultantCreateDate?: string | null
      laborConsultantName?: string | null
      laborConsultantAgentType?: 'submission_agent' | 'office_agent' | null
      laborConsultantPrefectureId?: string | null
      laborConsultantPhoneNumber0?: string | null
      laborConsultantPhoneNumber1?: string | null
      laborConsultantPhoneNumber2?: string | null
      version?: number | null
    }
  }
}

export const R510Report: FC<ReportFormProps> = ({procedureStatusId, submitText, afterSubmit}) => {
  const {data, loading} = useQuery<R510ReportQueryResult, R510ReportQueryVariables>(R510_REPORT, {
    variables: {
      procedureStatusId,
    },
    fetchPolicy: 'network-only',
  })
  const procedureStatus = data?.client?.procedureStatus

  if (loading || !procedureStatus) {
    return null
  }

  return (
    <div>
      <div className="l-title-wrap">
        <h1 className="m-title-main">雇用保険被保険者休業開始時賃金月額証明書</h1>
        <EmployeeName employee={procedureStatus.employee} />
      </div>
      <Form procedureStatus={procedureStatus} submitText={submitText} afterSubmit={afterSubmit} />
    </div>
  )
}

const schema = yup
  .object({
    r510PaymentParamSets: yup
      .array(
        yup
          .object({
            id: yup.string().notRequired(),
            rowNumber: yup.number().required(),
            insuredTermStartOn: yup.string().notRequired().dateFormat().label('被保険者期間算定対象期間 休業等開始日'),
            insuredTermEndOn: yup.string().notRequired().dateFormat().label('被保険者期間算定対象期間 休業等終了日'),
            insuredBaseDateNum: yup
              .number()
              .notRequired()
              .allowEmptyString()
              .typeError(({label}) => `${label}は半角数字で入力してください`)
              .integer(({label}) => `${label}は整数で入力してください`)
              .max(99, ({label}) => `${label}を2桁以内の半角数字で入力してください`)
              .label('被保険者期間算定対象期間基礎日数'),
            paymentTermStartOn: yup.string().notRequired().dateFormat().label('賃金支払対象期間'),
            paymentTermEndOn: yup.string().notRequired().dateFormat().label('賃金支払対象期間'),
            paymentBaseDateNum: yup
              .number()
              .notRequired()
              .allowEmptyString()
              .typeError(({label}) => `${label}は半角数字で入力してください`)
              .integer(({label}) => `${label}は整数で入力してください`)
              .max(99, ({label}) => `${label}を2桁以内の半角数字で入力してください`)
              .label('賃金支払対象期間基礎日数'),
            monthlyWage: yup
              .string()
              .notRequired()
              .matches(/^(\d{1,7}|未計算)$/, {
                message: ({label}) => `${label}は半角数値7桁以内か「未計算」と入力してください。`,
                excludeEmptyString: true,
              })
              .label('賃金額'),
            hourlyOrDailyWage: yup
              .string()
              .notRequired()
              .matches(/^(\d{1,7}|未計算)$/, {
                message: ({label}) => `${label}は半角数値7桁以内か「未計算」と入力してください。`,
                excludeEmptyString: true,
              })
              .label('賃金額'),
            notes: yup.string().notRequired().max(16).label('備考'),
          })
          .required()
      )
      .required()
      .label('給与情報')
      .formErrorsMeta({ordinalFormat: '$number番目'}),
    notesAboutSalary: yup.string().notRequired().max(140).label('賃金に関する特記事項'),
    hasContractPeriod: yup.string().notRequired().oneOf(['true', 'false']).label('（休業開始時における）雇用期間'),
    contractPeriodEndOn: yup.string().notRequired().dateFormat().label('雇用期間終了日'),
    workingYears: yup
      .number()
      .notRequired()
      .allowEmptyString()
      .typeError(({label}) => `${label}は半角数字で入力してください`)
      .integer(({label}) => `${label}は整数で入力してください`)
      .max(99, ({label}) => `${label}を2桁以内の半角数字で入力してください`)
      .label('勤続期間の年'),
    workingMonths: yup
      .number()
      .notRequired()
      .allowEmptyString()
      .typeError(({label}) => `${label}は半角数字で入力してください`)
      .integer(({label}) => `${label}は整数で入力してください`)
      .max(99, ({label}) => `${label}を2桁以内の半角数字で入力してください`)
      .label('勤続期間の月'),
  })
  .concat(laborConsultantSchema)

type Schema = yup.InferType<typeof schema>

const makeDefaultValues = (ps: R510ReportQueryResult['client']['procedureStatus']): Schema => {
  const r510 = ps.r510ParamSet

  return {
    r510PaymentParamSets: r510.r510PaymentParamSets.map((pps) => ({
      id: pps.id,
      rowNumber: pps.rowNumber,
      insuredTermStartOn: pps.insuredTermStartOn ? moment(pps.insuredTermStartOn).format('YYYY/MM/DD') : '',
      insuredTermEndOn: pps.insuredTermEndOn ? moment(pps.insuredTermEndOn).format('YYYY/MM/DD') : '',
      insuredBaseDateNum: pps.insuredBaseDateNum,
      paymentTermStartOn: pps.paymentTermStartOn ? moment(pps.paymentTermStartOn).format('YYYY/MM/DD') : '',
      paymentTermEndOn: pps.paymentTermEndOn ? moment(pps.paymentTermEndOn).format('YYYY/MM/DD') : '',
      paymentBaseDateNum: pps.paymentBaseDateNum,
      monthlyWage: pps.monthlyWage,
      hourlyOrDailyWage: pps.hourlyOrDailyWage,
      notes: pps.notes,
    })),
    contractPeriodEndOn: r510.contractPeriodEndOn ? moment(r510.contractPeriodEndOn).format('YYYY/MM/DD') : '',
    hasContractPeriod: r510.hasContractPeriod ? 'true' : 'false',
    notesAboutSalary: r510.notesAboutSalary || '',
    workingMonths: r510.workingMonths,
    workingYears: r510.workingYears,
    needLaborConsultant: r510.needLaborConsultant,
    laborConsultantCreateDate: r510.laborConsultantCreateDate || '',
    laborConsultantName: r510.laborConsultantName || '',
    laborConsultantAgentType: r510.laborConsultantAgentType || undefined,
    laborConsultantPrefectureId: r510.laborConsultantPrefecture?.id || undefined,
    laborConsultantPhoneNumber0: r510.laborConsultantPhoneNumber0 || '',
    laborConsultantPhoneNumber1: r510.laborConsultantPhoneNumber1 || '',
    laborConsultantPhoneNumber2: r510.laborConsultantPhoneNumber2 || '',
  }
}

interface FormProps {
  procedureStatus: R510ReportQueryResult['client']['procedureStatus']
  submitText: string
  afterSubmit: ReportFormProps['afterSubmit']
}

const Form: FC<FormProps> = ({procedureStatus, submitText, afterSubmit}) => {
  const dispatch = useDispatch()
  const methods = useForm<Schema>({
    defaultValues: makeDefaultValues(procedureStatus),
    resolver: yupResolver(schema),
    mode: 'onTouched',
    shouldFocusError: false,
  })
  const {
    register,
    handleSubmit,
    control,
    watch,
    getValues,
    setValue,
    setError,
    formState: {errors, isSubmitting},
  } = methods
  const {fields, append, remove} = useFieldArray({control, name: 'r510PaymentParamSets'})
  const hasContractPeriod = watch('hasContractPeriod')

  const [updateR510ParamSet] = useMutation<unknown, UpdateR510ParamSetQueryVariables>(UPDATE_R510_PARAM_SET)

  const onSubmit: SubmitHandler<Schema> = async (data) => {
    const blankToNull = (value: string | undefined | null) => (value ? value : null)

    try {
      await updateR510ParamSet({
        variables: {
          input: {
            procedureStatusId: procedureStatus.id,
            attributes: {
              r510PaymentParamSets: data.r510PaymentParamSets.map((pps) => ({
                rowNumber: pps.rowNumber,
                insuredTermStartOn: blankToNull(pps.insuredTermStartOn),
                insuredTermEndOn: blankToNull(pps.insuredTermEndOn),
                insuredBaseDateNum: pps.insuredBaseDateNum ?? null,
                paymentTermStartOn: blankToNull(pps.paymentTermStartOn),
                paymentTermEndOn: blankToNull(pps.paymentTermEndOn),
                paymentBaseDateNum: pps.paymentBaseDateNum ?? null,
                monthlyWage: blankToNull(pps.monthlyWage),
                hourlyOrDailyWage: blankToNull(pps.hourlyOrDailyWage),
                notes: blankToNull(pps.notes),
              })),
              notesAboutSalary: blankToNull(data.notesAboutSalary),
              hasContractPeriod: data.hasContractPeriod === 'true',
              contractPeriodEndOn: blankToNull(data.contractPeriodEndOn),
              workingYears: data.workingYears ?? null,
              workingMonths: data.workingMonths ?? null,
              needLaborConsultant: data.needLaborConsultant ?? false,
              laborConsultantCreateDate: blankToNull(data.laborConsultantCreateDate),
              laborConsultantName: blankToNull(data.laborConsultantName),
              laborConsultantAgentType: data.laborConsultantAgentType ?? null,
              laborConsultantPrefectureId: data.laborConsultantPrefectureId ?? null,
              laborConsultantPhoneNumber0: blankToNull(data.laborConsultantPhoneNumber0),
              laborConsultantPhoneNumber1: blankToNull(data.laborConsultantPhoneNumber1),
              laborConsultantPhoneNumber2: blankToNull(data.laborConsultantPhoneNumber2),
              version: procedureStatus.r510ParamSet.version,
            },
          },
        },
      })
    } catch (err) {
      if (err instanceof ServerValidationError) {
        eachValidationErrorsForHookFrom(err.validationErrors, schema, data, (path, error) => {
          setError(path as FieldPath<Schema>, error)
        })
        window.scrollTo({top: 0, behavior: 'smooth'})
        return
      }
      dispatch(asyncError(err))
      return
    }

    return await afterSubmit()
  }

  const handleSubmitError = () => {
    window.scrollTo({top: 0, behavior: 'smooth'})
  }
  const {confirmUpgrade, confirmUpgradeModal} = useConfirmUpgrade(
    procedureStatus.r510ParamSet?.needUpgrade,
    handleSubmit(onSubmit, handleSubmitError)
  )

  const startedOn = [procedureStatus.r509ParamSet, procedureStatus.r512ParamSet].find((ps) => ps?.id)?.startedOn

  useEffect(() => {
    if (hasContractPeriod === 'true') {
      const [workingYears, workingMonths] = getValues(['workingYears', 'workingMonths'])
      if (
        (typeof workingYears === 'number' && workingYears >= 0) ||
        (typeof workingMonths === 'number' && workingMonths >= 0)
      ) {
        return
      }

      const [years, months] = workingYearAndMonths(procedureStatus.employee.joinedAt, startedOn)
      setValue('workingYears', years, {shouldTouch: true, shouldDirty: true})
      setValue('workingMonths', months, {shouldTouch: true, shouldDirty: true})
    } else {
      setValue('contractPeriodEndOn', '', {shouldTouch: true, shouldDirty: true})
      setValue('workingYears', null, {shouldTouch: true, shouldDirty: true})
      setValue('workingMonths', null, {shouldTouch: true, shouldDirty: true})
    }
  }, [hasContractPeriod, setValue])

  const addPage = () => {
    makeRowsData(
      startedOn,
      procedureStatus.employee.joinedAt,
      procedureStatus.employee.currentOffice.salaryClosingDate,
      TWO_PAGE_ROWS
    )
      .slice(fields.length, TWO_PAGE_ROWS)
      .forEach((data) => append(data, {shouldFocus: false}))
  }

  const removePage = () => {
    for (let index = fields.length - 1; ONE_PAGE_ROWS <= index; index--) {
      remove(index)
    }
  }

  const recalculate = () => {
    const data = makeRowsData(
      startedOn,
      procedureStatus.employee.joinedAt,
      procedureStatus.employee.currentOffice.salaryClosingDate,
      fields.length > ONE_PAGE_ROWS ? TWO_PAGE_ROWS : ONE_PAGE_ROWS
    )
    setValue(
      'r510PaymentParamSets',
      fields.map((field, index) => defaults(data[index], field)),
      {shouldTouch: true, shouldDirty: true}
    )
  }

  return (
    <FormProvider {...methods}>
      <FormErrors errors={errors} isSubmitting={isSubmitting} schema={schema} prependFullPath />

      <Panel className={styles.panel}>
        <Body>
          <div className="l-overflow-scroll">
            <table className={styles.paymentTable}>
              <thead>
                <tr>
                  <th colSpan={2}>被保険者期間算定対象期間</th>
                  <th rowSpan={2}>賃金支払基礎日数</th>
                  <th rowSpan={2} colSpan={2}>
                    賃金支払対象期間
                  </th>
                  <th rowSpan={2}>基礎日数</th>
                  <th colSpan={3}>賃金額（円）</th>
                  <th rowSpan={2}>備考</th>
                </tr>
                <tr>
                  <th>休業等開始日</th>
                  <th>{startedOn && moment(startedOn).format('MM/DD')}</th>
                  <th>A</th>
                  <th>B</th>
                  <th>計</th>
                </tr>
              </thead>
              <tbody>
                {fields.map((field, index) => (
                  <tr key={field.id}>
                    <DateTimeTd name={`r510PaymentParamSets.${index}.insuredTermStartOn`} control={control} />
                    {index === 0 ? (
                      <td className={styles.firstRowEndOn}>休業等開始日の前日</td>
                    ) : (
                      <DateTimeTd name={`r510PaymentParamSets.${index}.insuredTermEndOn`} control={control} />
                    )}
                    <td
                      className={classNames(
                        styles.def,
                        errors.r510PaymentParamSets?.[index]?.insuredBaseDateNum && styles.invalid
                      )}
                    >
                      <input type="text" {...register(`r510PaymentParamSets.${index}.insuredBaseDateNum`)} />
                    </td>
                    <DateTimeTd name={`r510PaymentParamSets.${index}.paymentTermStartOn`} control={control} />
                    {index === 0 ? (
                      <td className={styles.firstRowEndOn}>休業等開始日の前日</td>
                    ) : (
                      <DateTimeTd name={`r510PaymentParamSets.${index}.paymentTermEndOn`} control={control} />
                    )}
                    <td
                      className={classNames(
                        styles.def,
                        errors.r510PaymentParamSets?.[index]?.paymentBaseDateNum && styles.invalid
                      )}
                    >
                      <input type="text" {...register(`r510PaymentParamSets.${index}.paymentBaseDateNum`)} />
                    </td>
                    <td
                      className={classNames(
                        styles.def,
                        errors.r510PaymentParamSets?.[index]?.monthlyWage && styles.invalid
                      )}
                    >
                      <input type="text" {...register(`r510PaymentParamSets.${index}.monthlyWage`)} />
                    </td>
                    <td
                      className={classNames(
                        styles.def,
                        errors.r510PaymentParamSets?.[index]?.hourlyOrDailyWage && styles.invalid
                      )}
                    >
                      <input type="text" {...register(`r510PaymentParamSets.${index}.hourlyOrDailyWage`)} />
                    </td>
                    <WageSumTd
                      control={control}
                      names={[
                        `r510PaymentParamSets.${index}.monthlyWage`,
                        `r510PaymentParamSets.${index}.hourlyOrDailyWage`,
                      ]}
                    />
                    <td
                      className={classNames(
                        styles.notes,
                        errors.r510PaymentParamSets?.[index]?.notes && styles.invalid
                      )}
                    >
                      <input type="text" {...register(`r510PaymentParamSets.${index}.notes`)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="u-mt20">
            {fields.length < TWO_PAGE_ROWS && (
              <ActionButton
                onClick={addPage}
                disabled={!startedOn}
                disabledReason="育児休業等開始年月日を入力してください"
              >
                ページを追加
              </ActionButton>
            )}
            {fields.length > ONE_PAGE_ROWS && (
              <ActionButton
                onClick={removePage}
                disabled={!startedOn}
                disabledReason="育児休業等開始年月日を入力してください"
              >
                ページを削除
              </ActionButton>
            )}
            <ActionButton
              className="u-ml10"
              onClick={recalculate}
              disabled={!startedOn}
              disabledReason="育児休業等開始年月日を入力してください"
            >
              日付を再計算する
            </ActionButton>
            <ActionButton
              as={Link}
              className="u-ml10"
              disabled={!startedOn}
              disabledReason="育児休業等開始年月日を入力してください"
              to={`/procedure_statuses/${procedureStatus.id}/r510_import_payments`}
            >
              日数・賃金額の一括登録
            </ActionButton>
          </div>

          <div className={styles.miscs}>
            <div className={styles.field}>
              <Label>賃金に関する特記事項</Label>
              <Input {...register('notesAboutSalary')} isError={!!errors.notesAboutSalary} />
              {errors.notesAboutSalary?.message && <ErrorMessage>{errors.notesAboutSalary.message}</ErrorMessage>}
            </div>

            <div className={styles.field}>
              <Label>（休業開始時における）雇用期間</Label>
              <RadioContainer>
                <Radio {...register('hasContractPeriod')} value="false">
                  定めなし
                </Radio>
                <Radio {...register('hasContractPeriod')} value="true">
                  定めあり
                </Radio>
              </RadioContainer>
            </div>

            <div className={styles.dateField}>
              <DateField
                name="contractPeriodEndOn"
                label="雇用期間終了日"
                error={errors.contractPeriodEndOn?.message}
                displayJpDate={false}
                disabled={hasContractPeriod !== 'true'}
              />
            </div>

            <div className={styles.field}>
              <Label>勤続期間</Label>
              <div className={styles.workingPeriod}>
                <Input
                  {...register('workingYears')}
                  isError={!!errors.workingYears}
                  disabled={hasContractPeriod !== 'true'}
                />
                <span>年</span>
                <Input
                  {...register('workingMonths')}
                  isError={!!errors.workingMonths}
                  disabled={hasContractPeriod !== 'true'}
                />
                <span>ヶ月</span>
              </div>
              {errors.workingYears?.message && <ErrorMessage>{errors.workingYears.message}</ErrorMessage>}
              {errors.workingMonths?.message && <ErrorMessage>{errors.workingMonths.message}</ErrorMessage>}
            </div>
          </div>

          <LaborConsultantSection
            className="u-mt30"
            procedureStatusId={procedureStatus.id}
            reportName="R510"
            prefix=""
          />

          <div className="u-ta-c u-mt50">
            <Button
              primary
              onClick={confirmUpgrade}
              disabled={isSubmitting || !startedOn}
              disabledReason={!startedOn ? '育児休業等開始年月日が設定されていないため保存できません' : null}
            >
              {submitText}
            </Button>
          </div>
        </Body>
      </Panel>

      {confirmUpgradeModal}
    </FormProvider>
  )
}

type DateTimeTdName =
  | `r510PaymentParamSets.${number}.insuredTermStartOn`
  | `r510PaymentParamSets.${number}.insuredTermEndOn`
  | `r510PaymentParamSets.${number}.paymentTermStartOn`
  | `r510PaymentParamSets.${number}.paymentTermEndOn`

const DateTimeTd: FC<UseControllerProps<Schema, DateTimeTdName>> = ({control, name}) => {
  const {field, fieldState} = useController({control, name})

  return (
    <td className={classNames(styles.date, fieldState.invalid && styles.invalid)}>
      <DateTime
        dateFormat="YYYY/MM/DD"
        timeFormat={false}
        value={String(field.value ?? '')}
        onChange={(value) =>
          typeof value === 'string' ? field.onChange(value) : field.onChange(value.format('YYYY/MM/DD'))
        }
        onClose={field.onBlur}
      />
    </td>
  )
}

interface WageSumTdProps {
  control: Control<Schema>
  names: FieldPath<Schema>[]
}

const WageSumTd: FC<WageSumTdProps> = ({control, names}) => {
  const wages = useWatch({control, name: names})
  const validated = wages.filter(isValidWage)

  return (
    <td className={styles.def}>
      {validated.length > 0 ? validated.reduce<number>((sum, wage) => sum + Number(wage), 0) : ''}
    </td>
  )
}

const isValidWage = (value: unknown): value is number | string =>
  (typeof value === 'number' && Number.isFinite(value) && value >= 0) ||
  (typeof value === 'string' && value.length > 0 && isValidWage(Number(value)))
